import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { PhoneIncoming, Circle, Pause, Play, X, User } from 'lucide-react';

/**
 * Polls the current user's active Yeastar call and shows a floating card with a
 * link to the matched customer plus live recording pause/resume controls.
 */
export const CallNotifier = () => {
  const [call, setCall] = useState(null);
  const [dismissed, setDismissed] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const poll = useCallback(async () => {
    try {
      const res = await api.get('/yeastar/active-call');
      if (res.data?.active) setCall(res.data);
      else setCall(null);
    } catch (e) {
      setCall(null);
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, [poll]);

  if (!call || dismissed === call.call_id) return null;

  const control = async (action) => {
    setBusy(true);
    try {
      await api.post(`/yeastar/calls/recording/${action}`, { channel_id: call.channel_id });
      toast.success(action === 'pause' ? 'Recording paused' : 'Recording resumed');
      setCall({ ...call, recording_paused: action === 'pause' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recording control failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] w-80 rounded-2xl bg-slate-900 text-white shadow-2xl border border-slate-700 overflow-hidden"
      data-testid="call-notifier"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-green-600">
        <div className="flex items-center gap-2">
          <PhoneIncoming className="w-4 h-4 animate-pulse" />
          <span className="text-sm font-semibold">
            {call.direction === 'outbound' ? 'On call' : 'Incoming call'}
          </span>
        </div>
        <button onClick={() => setDismissed(call.call_id)} className="text-white/80 hover:text-white" data-testid="call-notifier-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div>
          <p className="text-base font-medium">{call.customer_name || 'Unknown caller'}</p>
          <p className="text-xs text-slate-400">{call.peer_number}</p>
        </div>

        {call.customer_id && (
          <button
            onClick={() => navigate(`/customers/${call.customer_id}?tab=history`)}
            className="w-full inline-flex items-center justify-center gap-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-lg py-2 transition-colors"
            data-testid="call-notifier-open-customer"
          >
            <User className="w-4 h-4" /> Open customer
          </button>
        )}

        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-slate-300">
            <Circle className={`w-2.5 h-2.5 ${call.recording_paused ? 'text-slate-500' : 'text-red-500 fill-red-500 animate-pulse'}`} />
            {call.recording_paused ? 'Recording paused' : 'Recording'}
          </span>
          {call.recording_paused ? (
            <button
              onClick={() => control('resume')}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium bg-green-600 hover:bg-green-500 rounded-full px-3 py-1.5 transition-colors"
              data-testid="call-recording-resume"
            >
              <Play className="w-3.5 h-3.5" /> Resume
            </button>
          ) : (
            <button
              onClick={() => control('pause')}
              disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-500 rounded-full px-3 py-1.5 transition-colors"
              data-testid="call-recording-pause"
            >
              <Pause className="w-3.5 h-3.5" /> Pause recording
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallNotifier;
